/**
 * Admin Portal → Codes → Issue codes (Codes PR-8; the lead's unified-codes design §2.1). One place to
 * make every kind of code, by owner:
 *
 *   `/admin/codes/issue`          Summer50 and one-off codes — "Generate pool codes" and
 *                                 "+ One-off code" (with its Note), the code list with its Note
 *                                 column, the Summer50 dashboard (the page that was Users → Codes,
 *                                 `/admin/growth`, which redirects here)
 *   `/admin/codes/issue/house`    Coach Cedar's own codes (the house partner; was `/admin/codes/house`)
 *   `/admin/codes/issue/partner`  a partner's codes: pick the partner, then make codes on one of its links
 *
 * `?open=one-off` opens the one-off form straight away (the Overview's "+ One-off code" button).
 * Each tab keeps its own role rules: every write needs an admin role and stays hidden until the role
 * is known (fail closed).
 *
 * Web-only (the admin SPA).
 */

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import { ReadOnlyNote, errText, hint, td, th } from '../partners/PartnersShared';
import type { PartnerRow } from '../partners/partnersLogic';
import { AdminGrowth } from './AdminGrowth';
import { AdminHouseCodes } from './AdminHouseCodes';
import { CODES_ROUTES, ISSUE_RULES } from './codesAreaLogic';
import { CodesHeader, ErrorLine, PAGE, SubTabs, useCodesRole } from './CodesArea';

const TABS = [
  { to: CODES_ROUTES.issue, label: 'Summer50 & one-off', exact: true },
  { to: CODES_ROUTES.issueHouse, label: 'Coach Cedar' },
  { to: CODES_ROUTES.issuePartner, label: 'A partner' },
];

export function CodesIssue({ owner = 'campaign' }: { owner?: 'campaign' | 'house' | 'partner' }) {
  const [search] = useSearchParams();
  return (
    <>
      <div style={{ ...PAGE, paddingBottom: 0 }}>
        <CodesHeader title="Issue codes" subtitle="Make codes for a Summer50 wave, one named person, Coach Cedar's own campaigns, or a partner." />
        <SubTabs tabs={TABS} />
        <ul data-testid="issue-rules" style={{ ...hint, margin: '0 0 8px', paddingLeft: 18 }}>
          {ISSUE_RULES.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </div>
      {owner === 'campaign' && <AdminGrowth embedded openOneOff={search.get('open') === 'one-off'} />}
      {owner === 'house' && <AdminHouseCodes />}
      {owner === 'partner' && <PartnerPicker />}
    </>
  );
}

/** A partner's codes are made on one of its links (Links & tags → a tag → "Make codes"). */
const PartnerPicker: React.FC = () => {
  const { role, loaded, writable } = useCodesRole();
  const [partners, setPartners] = useState<PartnerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    PartnersAdmin.listPartners()
      .then((r) => { if (live) setPartners(r.partners || []); })
      .catch((e) => { if (live) setError(errText(e)); });
    return () => { live = false; };
  }, []);
  return (
    <div style={{ ...PAGE, paddingTop: 0 }}>
      <ReadOnlyNote writable={writable} loaded={loaded} role={role} subject="codes" />
      <ErrorLine text={error} />
      <AdminCard>
        <p style={{ ...hint, marginTop: 0 }}>
          A partner&apos;s codes belong to one of its links: open the partner, choose the link on Links &amp; tags, then
          &quot;Make codes&quot; — a shared code, or a batch of single-use codes with its purpose and notes.
        </p>
        {partners && partners.length === 0 && <div style={hint}>No partners yet: add one on Partners.</div>}
        {partners && partners.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="partner-picker">
            <thead><tr><th style={th}>Partner</th><th style={th}>Code</th><th style={th}>Status</th><th style={th}>Links</th><th style={th} /></tr></thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id}>
                  <td style={td}>{p.display_name}</td>
                  <td style={td}>{p.code}</td>
                  <td style={td}>{p.status}</td>
                  <td style={td}>{p.tag_count}</td>
                  <td style={td}><Link to={`/admin/partners/${encodeURIComponent(p.id)}?tab=tags`}>{writable ? 'Make codes →' : 'Open →'}</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </AdminCard>
    </div>
  );
};

export default CodesIssue;
